import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users, TrendingUp, TrendingDown, ClipboardList,
  Phone, Mail, MapPin, Calendar, Plus, Building2
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis
} from "recharts";

const activityTypeColors = {
  call: "#3b82f6",
  email: "#6366f1",
  meeting: "#8b5cf6",
  visit: "#f59e0b",
  follow_up: "#10b981",
};

const winRateColors = { Won: "#10b981", Active: "#3b82f6", Lost: "#ef4444" };

// Custom tooltip for pipeline bar chart
export const PipelineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
  return (
    <div className="bg-[#121215] border border-white/10 rounded-sm px-3 py-2 text-xs">
      <p className="font-bold text-white mb-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{label}</p>
      <p className="text-muted-foreground">Count: <span className="text-white">{payload[0]?.value}</span></p>
      {payload[0]?.payload?.value > 0 && (
        <p className="text-muted-foreground">Value: <span className="text-amber-400">{gbp.format(payload[0].payload.value)}</span></p>
      )}
    </div>
  );
};

// Delta trend badge used on metric widgets
export const DeltaBadge = ({ value }) => {
  if (value == null || value === 0) return null;
  const positive = value > 0;
  return (
    <div className={`flex items-center gap-1 mt-1 ${positive ? "text-emerald-400" : "text-red-400"}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      <span className="text-[10px] font-semibold">{positive ? "+" : ""}{value}%</span>
    </div>
  );
};

// Win rate donut chart card
export const WinRateChart = ({ wonCount, lostCount, activeCount }) => {
  const totalClosed = wonCount + lostCount;
  const winRate = totalClosed > 0 ? Math.round((wonCount / totalClosed) * 100) : 0;

  const data = [
    { name: "Won", value: wonCount },
    { name: "Active", value: activeCount },
    { name: "Lost", value: lostCount },
  ].filter((d) => d.value > 0);

  return (
    <Card className="border-white/5 bg-card rounded-sm animate-fade-slide-up" style={{ animationDelay: "440ms" }}>
      <CardHeader className="pb-2 px-4 md:px-6">
        <CardTitle className="text-xs md:text-sm font-bold tracking-widest uppercase text-muted-foreground">
          Win Rate
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 md:px-6" data-testid="win-rate-chart">
        {data.length > 0 ? (
          <>
            <div className="relative">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={78}
                    dataKey="value"
                    strokeWidth={0}
                    isAnimationActive={true}
                  >
                    {data.map((entry) => (
                      <Cell key={entry.name} fill={winRateColors[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#121215", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", fontSize: "12px" }}
                    itemStyle={{ color: "#a1a1aa" }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Center label overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                  {winRate}%
                </span>
                <span className="text-[9px] text-muted-foreground tracking-widest uppercase">Win Rate</span>
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {data.map((entry) => (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: winRateColors[entry.name] }} />
                  <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">No closed deals yet</div>
        )}
      </CardContent>
    </Card>
  );
};

// Activities timeline component
export const ActivitiesTimeline = ({ activities }) => {
  const now = new Date();
  const typeIcons = { call: Phone, email: Mail, meeting: Users, visit: MapPin, follow_up: Calendar };

  if (!activities?.length) {
    return <div className="py-6 text-center text-xs text-muted-foreground">No tasks due this week</div>;
  }

  return (
    <div className="space-y-0" data-testid="activities-timeline">
      {activities.map((act, idx) => {
        const dueDate = act.due_date ? new Date(act.due_date) : null;
        const isOverdue = dueDate && dueDate < now;
        const accentColor = activityTypeColors[act.activity_type] || "#3b82f6";
        const Icon = typeIcons[act.activity_type] || ClipboardList;
        const isLast = idx === activities.length - 1;

        return (
          <div key={act.id} className="flex gap-3" data-testid={`activity-item-${act.id}`}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div
                className="w-2 h-2 rounded-full mt-3 flex-shrink-0"
                style={{ background: isOverdue ? "#ef4444" : accentColor }}
              />
              {!isLast && (
                <div className="w-px flex-1 mt-1" style={{ background: "rgba(255,255,255,0.05)" }} />
              )}
            </div>
            <div className="flex-1 flex items-start justify-between pb-3 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <Icon
                  className="w-3.5 h-3.5 flex-shrink-0"
                  style={{ color: isOverdue ? "#ef4444" : accentColor }}
                  strokeWidth={1.5}
                />
                <div className="min-w-0">
                  <p className={`text-xs font-medium truncate ${isOverdue ? "text-red-300" : "text-foreground"}`}>
                    {act.title}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{act.company_name}</p>
                </div>
              </div>
              {dueDate && (
                <Badge
                  variant="outline"
                  data-testid={`activity-date-${act.id}`}
                  className={`text-[10px] rounded-sm flex-shrink-0 ml-2 ${
                    isOverdue
                      ? "border-red-500/30 text-red-400 bg-red-500/10"
                      : "border-white/10 text-amber-400"
                  }`}
                >
                  {isOverdue ? "Overdue" : dueDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Dashboard skeleton loading state
export const DashboardSkeleton = () => (
  <div data-testid="dashboard-loading">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-3">
      <div>
        <Skeleton className="h-10 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-10 w-28" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="border-white/5 bg-card rounded-sm">
          <CardContent className="p-5">
            <Skeleton className="h-3 w-24 mb-3" />
            <Skeleton className="h-8 w-20 mb-2" />
            <Skeleton className="h-3 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
      <Card className="lg:col-span-2 border-white/5 bg-card rounded-sm">
        <CardContent className="p-5">
          <Skeleton className="h-3 w-32 mb-4" />
          <Skeleton className="h-[220px] w-full" />
        </CardContent>
      </Card>
      <Card className="border-white/5 bg-card rounded-sm">
        <CardContent className="p-5">
          <Skeleton className="h-3 w-28 mb-4" />
          <Skeleton className="h-[180px] w-full" />
        </CardContent>
      </Card>
    </div>
  </div>
);

// Empty state card
export const DashboardEmptyState = ({ onAddLead }) => (
  <div data-testid="dashboard-empty" className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 rounded-sm bg-blue-500/10 flex items-center justify-center mb-5">
      <Building2 className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
    </div>
    <h2 className="text-2xl font-bold tracking-tight uppercase mb-2" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
      No leads yet
    </h2>
    <p className="text-sm text-muted-foreground mb-6 max-w-xs">
      Add your first lead to start tracking your pipeline
    </p>
    <Button
      data-testid="empty-state-add-lead"
      onClick={onAddLead}
      className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10 px-6"
    >
      <Plus className="w-4 h-4 mr-2" /> Add Lead
    </Button>
  </div>
);
